import { Box } from '@material-ui/core';
import { ClassNameMap } from '@material-ui/styles';
import { CheckoutSessionCriteria, formatDateTimeReadable, formatMoney } from '@nemuru-eng/js-api-client';
import { useEffect } from 'react';
import { generatePath } from 'react-router-dom';

import { getCardHeaderParamsByApplicationType } from '@/components/shared/components/ConditionsSummary/LoanConditionsSummary/config';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { Font } from '@/components/shared/components/Font';
import { StatusTag } from '@/components/shared/components/StatusTag/StatusTag';
import { LinkToDetailButton } from '@/components/shared/components/SuperSearchBar/components/SearchResultCard/components/shared/LinkToDetailButton';
import { SearchBarFilterOptions } from '@/components/shared/components/SuperSearchBar/types/SearchBarFilterOptions';
import { Text } from '@/components/shared/components/Text';
import { DYNAMIC_PAGE_PATHS } from '@/components/views/DynamicPageView/config/shared/pathRoutes';
import { ICON_NAMES } from '@/config/config';
import { ApplicationTypeMap } from '@/config/maps/ApplicationType.map';
import { ApplicationType, StatusTagType } from '@/config/types';
import { STYLE_PARAMS_BY_STATUS } from '@/constants/styleParamsByStatus';
import { useUser } from '@/hooks/useUser';
import { getFullNameString } from '@/utils/getFullName';
import { amountToValueMoneyMapper } from '@/utils/mappers/amountToValueMoneyMapper';

interface CheckoutSessionSearchResultCardProps {
  classes: ClassNameMap<'root'>;
  selected: boolean;
  searchResult: CheckoutSessionCriteria;
  toggleDialog: () => void;
  searchBarState: SearchBarFilterOptions;
  onNavigate?: boolean;
}

export const CheckoutSessionSearchResultCard = ({
  classes,
  selected,
  searchResult,
  toggleDialog,
  searchBarState,
  onNavigate,
}: CheckoutSessionSearchResultCardProps) => {
  const { isUserAdmin } = useUser();

  const applicationType = searchResult.selectedPaymentMethodType
    ? ApplicationTypeMap.fromPaymentMethodType(searchResult.selectedPaymentMethodType)
    : undefined;

  const isCard = applicationType === ApplicationType.CARD;

  const displayValues = {
    borrowerFullName: getFullNameString(searchResult.customer?.fullName) || 'PENDING_INFORMATION',
    principal: searchResult.principal,
    status: searchResult.status,
    iconName: applicationType
      ? getCardHeaderParamsByApplicationType(applicationType).iconName
      : ICON_NAMES.CHECKOUT_SESSION,
  };

  const onClick = () => {
    window.location.replace(
      generatePath(DYNAMIC_PAGE_PATHS.CHECKOUT_SESSION_DETAIL, {
        checkoutSessionId: searchResult.id,
      }),
    );
    toggleDialog();
  };

  useEffect(() => {
    if (!selected || !onNavigate) return;
    onClick();
  }, [onNavigate]);

  if (!displayValues) {
    return null;
  }

  return (
    <Box className={'appear-anim'}>
      <CustomPaper
        fill={selected ? 'grey.dark' : 'grey.light'}
        style={{ padding: 8, cursor: 'pointer' }}
        className={classes.root}
        onClick={onClick}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box className={'overflow-text-ellipsis'} display={'flex'} flexDirection={'row'} alignItems={'center'}>
            <Box display={'flex'} flexDirection={'column'} pl={1} className={'overflow-text-ellipsis'}>
              <Font variant={'body2'} color={'title'} className={'overflow-text-ellipsis'}>
                {!isCard ? (
                  <Text
                    strong
                    tId={'DYNAMIC_CUSTOMER_NAME_WITH_APPLICATION_AMOUNT'}
                    dynamicDataTexts={[
                      displayValues.borrowerFullName,
                      formatMoney(amountToValueMoneyMapper(displayValues.principal)),
                    ]}
                  />
                ) : (
                  <strong>{displayValues.borrowerFullName}</strong>
                )}
              </Font>
              <Font
                variant={'caption'}
                color={'textSecondary'}
                className={'overflow-text-ellipsis'}
              >{`${formatDateTimeReadable(searchResult[searchBarState.dateToShow])} | ${
                searchResult.cart.merchantOrderReference1
              }${isUserAdmin ? ` | ${searchResult.businessName}` : ''}`}</Font>
            </Box>
          </Box>
          <Box display={'flex'} flexDirection={'row'} alignItems={'center'} ml={2}>
            <StatusTag
              labelTextId={STYLE_PARAMS_BY_STATUS[displayValues.status]?.title}
              type={STYLE_PARAMS_BY_STATUS[displayValues.status]?.colorPaletteName ?? StatusTagType.DEFAULT}
              colorType={STYLE_PARAMS_BY_STATUS[displayValues.status]?.colorType ?? StatusTagType.DEFAULT}
            />
            <LinkToDetailButton onClick={onClick} />
          </Box>
        </Box>
      </CustomPaper>
    </Box>
  );
};
